import { useEffect, useState } from 'react';
import { assetsApi, type AssetCategory } from '../lib/api/assets';

interface Props {
  open: boolean;
  editing?: AssetCategory | null;
  onClose: () => void;
  onSaved: () => void;
}

export function AssetCategoryFormModal({ open, editing, onClose, onSaved }: Props) {
  const isEdit = Boolean(editing);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [account, setAccount] = useState('2111');
  const [years, setYears] = useState(4);
  const [method, setMethod] = useState('Đường thẳng');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setCode(editing.code);
      setName(editing.name);
      setAccount(editing.account);
      setYears(editing.years);
      setMethod(editing.method || 'Đường thẳng');
    } else {
      setCode(''); setName(''); setAccount('2111'); setYears(4); setMethod('Đường thẳng');
    }
    setErr(null); setSaving(false);
  }, [open, editing]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!isEdit && !/^[A-Z]{1,10}$/.test(code.trim().toUpperCase())) {
      return setErr('Mã loại 1-10 ký tự chữ cái (VD MT).');
    }
    if (name.trim().length < 2) return setErr('Tên loại tối thiểu 2 ký tự.');
    if (!/^\d{3,20}$/.test(account.trim())) return setErr('TK hạch toán phải là số (VD 2111).');
    if (years < 1 || years > 50) return setErr('Số năm khấu hao 1-50.');
    setSaving(true);
    try {
      if (isEdit && editing) {
        await assetsApi.updateCategory(editing.code, {
          name: name.trim(), account: account.trim(), years, method,
        });
      } else {
        await assetsApi.createCategory({
          code: code.trim().toUpperCase(), name: name.trim(), account: account.trim(), years, method,
        });
      }
      onSaved();
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : String(e2)); }
    finally { setSaving(false); }
  };

  return (
    <div className="cvm-backdrop" onClick={onClose}>
      <div className="cvm-modal afm-modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="cvm-head">
            <div>
              <div className="cvm-crumb">KẾ TOÁN › TÀI SẢN › DANH MỤC LOẠI</div>
              <h2>{isEdit ? `Sửa loại ${editing?.code}` : 'Thêm loại tài sản mới'}</h2>
              <p>Định nghĩa TK hạch toán + thời gian khấu hao mặc định (theo TT45/2013/TT-BTC).</p>
            </div>
            <button type="button" className="cvm-close" onClick={onClose}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg></button>
          </div>
          <div className="cvm-body">
            <div className="afm-grid-2">
              <div className="pfm-field">
                <label>Mã loại <span className="req">*</span></label>
                <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="VD PL, DR" maxLength={10} required disabled={isEdit} />
              </div>
              <div className="pfm-field">
                <label>TK hạch toán <span className="req">*</span></label>
                <input value={account} onChange={(e) => setAccount(e.target.value.replace(/\D/g, ''))} placeholder="2111" required inputMode="numeric" />
              </div>
            </div>
            <div className="pfm-field">
              <label>Tên loại <span className="req">*</span></label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="VD Phần cứng khác / Drone / …" required />
            </div>
            <div className="afm-grid-2">
              <div className="pfm-field">
                <label>Số năm khấu hao <span className="req">*</span></label>
                <input type="number" min={1} max={50} value={years} onChange={(e) => setYears(Math.max(1, Math.min(50, Number(e.target.value) || 1)))} required />
              </div>
              <div className="pfm-field">
                <label>Phương pháp KH</label>
                <select value={method} onChange={(e) => setMethod(e.target.value)}>
                  <option value="Đường thẳng">Đường thẳng</option>
                  <option value="Số dư giảm dần">Số dư giảm dần</option>
                  <option value="Theo sản lượng">Theo sản lượng</option>
                </select>
              </div>
            </div>
            {err && <div className="cvm-error">{err}</div>}
          </div>
          <div className="cvm-foot">
            <button type="button" className="pay-btn-outline" onClick={onClose} disabled={saving}>Huỷ</button>
            <button type="submit" className="pay-btn-primary" disabled={saving}>{saving ? 'Đang lưu…' : (isEdit ? 'Lưu thay đổi' : 'Thêm loại')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
